import { forwardRef, ReactChild, ReactNode } from 'react'
import * as RadixTabs from '@radix-ui/react-tabs'
import cx from 'classnames'

export const addCountToTabName = ({
  count,
  tabName,
}: {
  count: number
  tabName: string
}) => (count > 0 ? `${tabName} (${count})` : tabName)

export type Tab<T = string> = {
  key: T
  name: ReactNode
  menu?: ReactNode
  content: ReactChild
  destroyInactiveTabPane?: boolean
  isDisabled?: boolean
}

interface Props<T extends string> {
  tabs: Tab<T>[]
  activeKey?: T
  defaultActiveKey?: T
  onTabClick?: (activeKey: T, e: React.KeyboardEvent | React.MouseEvent) => void
  variant: 'inPage' | 'modal' | 'filter'
  hasContentMargin?: boolean
  className?: string
  destroyInactiveTabPane?: boolean
  moreIcon?: ReactNode
  'data-testid'?: string
}

const Tabs = <T extends string>(
  {
    tabs,
    onTabClick,
    defaultActiveKey,
    activeKey,
    variant,
    hasContentMargin = true,
    className,
    destroyInactiveTabPane = true,
    'data-testid': dataTestid,
  }: Props<T>,
  ref: React.Ref<HTMLDivElement> | undefined
) => {
  const isModal = variant === 'modal'
  const isInPage = variant === 'inPage'
  const isFilter = variant === 'filter'

  return (
    <RadixTabs.Root
      ref={ref}
      value={activeKey}
      defaultValue={defaultActiveKey}
      onValueChange={(value) =>
        onTabClick?.(value as T, {} as React.MouseEvent)
      }
      className={cx(
        'updated-component',
        {
          'flex-1 bg-gold-40': isInPage,
          'flex-1 bg-white flex flex-col items-stretch h-full': isModal,
          'flex-1': isFilter,
        },
        className
      )}
      data-testid={dataTestid}
    >
      <RadixTabs.List
        className={cx('flex', {
          'max-w-[690px] relative top-[1px] border-b border-coolGray-200':
            isInPage,
          'border-b border-coolGray-300 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]':
            isModal,
          'overflow-x-auto relative top-[1px]': isFilter,
        })}
      >
        {tabs.map(({ key, name, menu, isDisabled = false }, index) => (
          <RadixTabs.Trigger
            key={key}
            value={key}
            disabled={isDisabled}
            data-cy={`tab-${index}`}
            data-testid={`tab-${index}`}
            className={cx({
              'cursor-pointer px-4 py-1 pt-4 bg-gray-50 select-none capitalize text-base font-medium text-coolGray-400 data-[state=active]:text-maroon-400 data-[state=active]:border-b-2 data-[state=active]:border-maroon-400 data-[disabled]:cursor-not-allowed':
                isInPage,
              'w-[418px] py-5 font-bold text-base border-r border-coolGray-300 text-coolGray-500 relative data-[state=active]:text-maroon-400 data-[disabled]:cursor-not-allowed after:absolute after:bottom-[-1px] after:left-0 after:right-0 data-[state=active]:after:h-[2px] data-[state=active]:after:bg-white':
                isModal,
              'text-sm font-medium text-gold-700 px-4 py-2 mx-1 uppercase border-b-2 border-transparent cursor-pointer hover:text-base-700 data-[state=active]:text-base-700 data-[state=active]:font-bold data-[state=active]:border-base-700 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40':
                isFilter,
            })}
          >
            {isModal && (
              <span className="pr-4 ml-6 inline-block">{index + 1}.</span>
            )}
            {menu ?? <span className="capitalize">{name}</span>}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {tabs.map(({ key, content }) => (
        <RadixTabs.Content
          key={key}
          value={key}
          {...(!destroyInactiveTabPane ? { forceMount: true } : {})}
          className={cx({
            'bg-gold-40': isInPage,
            'flex flex-col flex-1 min-h-0 h-full': isModal,
          })}
        >
          {isInPage || isFilter ? (
            <div className={cx({ 'mt-9': hasContentMargin && isInPage })}>
              {content}
            </div>
          ) : (
            content
          )}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  )
}

export default forwardRef(Tabs)
